import { Box } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { convertToGeoLocation } from "@src/appV2/Location";
import { BookabilityDecision } from "@src/appV2/OpenShifts/ShiftAction/types";
import { List } from "@src/appV2/redesign/system/ui-components";
import { memo } from "react";
import { useHistory } from "react-router-dom";

import { PageWrapper } from "../../components/PageWrapper";
import { ShiftBottomSheetBookabilityCardsContainer } from "../../Shift/BottomSheet/BookabilityCardsContainer";
import { ShiftBottomSheetFooter } from "../../Shift/BottomSheet/Footer";
import { ShiftBottomSheetHeader } from "../../Shift/BottomSheet/Header";
import { useFooterData } from "../../Shift/BottomSheet/useFooterData";
import { ShiftDurationListItem } from "../../Shift/DurationListItem";
import { ShiftHighlightsAndMapListItem } from "../../Shift/HighlightsAndMapListItem";
import { type OpenShiftListItem, type OpenShiftWorkplace } from "../../Shift/Open/types";
import { convertShiftToSlotId, type ShiftSlotCoalesced } from "../../Shift/Open/useGetShiftsSlotV2";
import { resolveShiftType } from "../../Shift/resolveShiftType";
import { ShiftWorkplaceListItem } from "../../Shift/WorkplaceListItem";
import { getShiftWorkDurationInHours } from "../../utils/getShiftWorkDuration";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { WorkingThisShiftListItem } from "../../WorkWithFriends/components/WorkingThisShiftListItem";
import { type WorkerPublicProfile } from "../../WorkWithFriends/hooks/useGetWorkerPublicProfile";
import { WorkWithFriendsProvider } from "../../WorkWithFriends/WorkWithFriendsContext";

export interface ShiftRecommendationsProps {
  storyShift: OpenShiftListItem;
  storyWorkplace: OpenShiftWorkplace;
  shiftSlots: Record<string, ShiftSlotCoalesced>;
  friendsMap: Map<string, WorkerPublicProfile>;
  outboundFriendRequestsSet: Set<string>;
}

export const ShiftRecommendationsCard = memo((props: ShiftRecommendationsProps) => {
  const { storyShift, storyWorkplace, shiftSlots, friendsMap, outboundFriendRequestsSet } = props;

  const { isBookable } = useFooterData();

  const history = useHistory();

  if (storyShift?.type !== "open-shift" || !storyWorkplace) {
    return <Box />;
  }

  return (
    <PageWrapper
      variant="tertiary"
      sx={{
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <ShiftBottomSheetHeader
          hideRightAction={false}
          shiftSlots={
            shiftSlots[
              convertShiftToSlotId({
                start: storyShift.attributes.start,
                end: storyShift.attributes.end,
                facilityId: storyWorkplace.id,
              })
            ]
          }
          onClose={() => {
            history.push(`${RootPaths.APP_V2_HOME}`);
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        <PageContent>
          <ShiftBottomSheetBookabilityCardsContainer
            shiftId={storyShift.id}
            shiftQualification={storyShift.attributes.qualification}
            shiftType={resolveShiftType({
              isUrgent: storyShift.attributes.isUrgent,
              window: storyShift.attributes.window,
              priorityTill: storyShift.attributes.priorityTill,
            })}
            shiftStartIso={storyShift.attributes.start}
            workplace={storyWorkplace}
            bookabilityStatus={{
              type: "shift",
              id: storyShift.id,
              attributes: {
                bookability: {
                  decision: BookabilityDecision.ALLOWED,
                },
              },
            }}
            hasHoursRestrictionConflict={false}
            shiftFailedLoading={false}
            hasMarkedInterest={false}
          />

          {
            <List sx={{ px: 6 }}>
              <ShiftDurationListItem
                shiftTimeSlot={storyShift.attributes.timeSlot}
                shiftStartIso={storyShift.attributes.start}
                shiftEndIso={storyShift.attributes.end}
                shiftDurationInHours={getShiftWorkDurationInHours(
                  { start: storyShift.attributes.start, end: storyShift.attributes.end },
                  false,
                  30
                )}
                workplaceTimeZone={storyWorkplace.attributes.location.timezone ?? ""}
              />

              <ShiftWorkplaceListItem
                workplaceId={storyWorkplace.id}
                workplaceName={storyWorkplace.attributes.name}
                workplaceType={storyWorkplace.attributes.type ?? ""}
                workplaceLocation={convertToGeoLocation([
                  storyWorkplace.attributes.location.longitude,
                  storyWorkplace.attributes.location.latitude,
                ])}
                onClick={() => {
                  // No-op
                }}
              />

              <ShiftHighlightsAndMapListItem
                isMapEnabled
                workplaceId={storyWorkplace.id}
                workplaceName={storyWorkplace.attributes.name}
                workplaceLocation={convertToGeoLocation([
                  storyWorkplace.attributes.location.longitude,
                  storyWorkplace.attributes.location.latitude,
                ])}
                workplaceFormattedAddress={undefined}
                isDividerEnabled={false}
              />

              <WorkWithFriendsProvider>
                <WorkingThisShiftListItem
                  shiftSlots={
                    shiftSlots[
                      convertShiftToSlotId({
                        start: storyShift.attributes.start,
                        end: storyShift.attributes.end,
                        facilityId: storyWorkplace.id,
                      })
                    ]
                  }
                  shiftId={storyShift.id}
                  friendsMap={friendsMap}
                  outboundFriendRequestsSet={outboundFriendRequestsSet}
                  isBookable={isBookable}
                />
              </WorkWithFriendsProvider>
            </List>
          }
        </PageContent>
      </Box>

      <Box sx={{ pointerEvents: "auto", zIndex: 1001 }}>
        <ShiftBottomSheetFooter />
      </Box>
    </PageWrapper>
  );
});

ShiftRecommendationsCard.displayName = "StoryCard";
